import { useEffect } from 'react';
import { useWebSocket } from './useWebSocket';
import { ChangePaymentStatusEvent, Order } from '@interfaces/OrderTypes';
import { SOCKET_EVENTS, ORDER_PAYMENT_MAP } from '@utils/constants';
import { useGetOrdersWithStatus } from '@pages/Orders/hooks/orders';
import { isEventForCurrentCommerce } from '@utils/hooks/eventCommerceScope';

export const useChangePaymentStatus = () => {
  const { subscribeEvent, unsubscribeEvent } = useWebSocket();
  const { orders, handleUpdateOrder, handleAddOrder } = useGetOrdersWithStatus();

  useEffect(() => {
    const onChangePaymentStatus = async (event: ChangePaymentStatusEvent) => {
      const existingOrder = orders.find((item) => item.id === event.orderId);

      if (existingOrder) {
        // Actualizar pedido existente con los nuevos datos de pago
        handleUpdateOrder({
          ...existingOrder,
          estatus: event.estatus,
          estatus_pago: event.estatus_pago,
          payment_provider_code: event.paymentProviderCode,
          payment_provider_payment_id: event.paymentProviderId,
          payment_provider_reason: event.paymentProviderReason,
        });
      } else {
        // Multisucursal (GAP-3): no crear "orden fantasma" de OTRA sucursal en el Kanban actual.
        // Los eventos de pago llegan por el canal de cuenta; solo materializar el pedido si es del
        // comercio activo (o si el evento no trae commerceId). El evento newOrder traerá el resto.
        if (!isEventForCurrentCommerce(event.commerceId, localStorage.getItem('id_commerce'))) return;
        // Crear pedido nuevo si no existe (primer evento de pre_checkout)
        // Solo crear con datos mínimos, el evento newOrder traerá los datos completos
        const newOrder: Order = {
          id: event.orderId,
          folio: event.folio,
          id_comercio: event.commerceId,
          estatus: event.estatus,
          estatus_pago: event.estatus_pago,
          estatus_pedido: event.estatus_pedido,
          nombre: event.customerName,
          numero_celular: event.customerPhone,
          country_code: event.customerCountryCode,
          metodo_pago: event.paymentMethod as keyof typeof ORDER_PAYMENT_MAP,
          payment_provider_code: event.paymentProviderCode,
          payment_provider_payment_id: event.paymentProviderId,
          payment_provider_reason: event.paymentProviderReason,
          // Campos requeridos con valores por defecto
          metodo_entrega: 'delivery',
          propina: 0,
          costo_envio: 0,
          subtotal: 0,
          total: 0,
          created_at: new Date().toISOString(),
          navegador: '',
          colonia: '',
          calle: '',
          direccion_completa: '',
          numero_interior: '',
          referencias: '',
          // Campos opcionales
          delivery_type: undefined,
          cliente_paga_con: undefined,
          coupon_code: undefined,
          coupon_type: undefined,
          coupon_value: undefined,
          coupon_amount: undefined,
          numero_mesa: undefined,
          has_requested_rider: undefined,
          updated_at: undefined,
          link_clip: undefined,
          comentario: undefined,
          enlace_pago: undefined,
          numero_exterior: undefined,
          link_google_maps: undefined,
          delivery_driver_info: undefined,
          delivery_tracking_id: undefined,
          delivery_tracking_url: undefined,
          delivery_status: null,
          origin: undefined,
          self_service: undefined,
        };

        handleAddOrder(newOrder);
      }
    };

    subscribeEvent(SOCKET_EVENTS.CHANGE_PAYMENT_STATUS, onChangePaymentStatus);

    return () => {
      unsubscribeEvent(SOCKET_EVENTS.CHANGE_PAYMENT_STATUS, onChangePaymentStatus);
    };
  }, [orders, handleUpdateOrder, handleAddOrder, subscribeEvent, unsubscribeEvent]);
};
